import {useState} from 'react';

import {useOnMount} from 'shared/web';

import {filterCategoriesBySearch} from '../components/Sidebar/components/FilterContentArea/utils/filterCategoriesBySearch';
import {getCategoryColor, type CategoryColorScheme} from '../constants/getCategoryColor';
import type {EagColumn} from '../types/EagColumn';
import type {EagColumnGroup, EagColumnOrGroup} from '../types/EagColumnGroup';
import type {Filters} from '../types/Filters';
import {isEagColumnGroup} from '../utils/isEagColumnGroup';
import {isFilterValueActive} from '../utils/isFilterValueActive';
import {normalizeSearchText} from '../utils/normalizeSearchText';

/**
 * Sentinel category id for top-level (ungrouped) filterable columns, rendered
 * under STANDALONE_CATEGORY_LABEL.
 */
export const STANDALONE_CATEGORY_ID = '__standalone__';

/**
 * Display label of the standalone category (not yet translated). The filter
 * panel search matches it like any group label, so "other" surfaces every
 * ungrouped filter.
 */
export const STANDALONE_CATEGORY_LABEL = 'Other';

export type CategoryItem = {
  id: string;
  label: string;
  color: CategoryColorScheme;
  activeCount: number;
  /** headerNames of all filterable children, normalised (lowercase, diacritics stripped) for search. */
  childLabels: string[];
};

type UseFilterCategorySelectionParams = {
  columnConfig: EagColumnOrGroup[];
  filterState: Filters;
  searchTerm: string;
};

type UseFilterCategorySelectionResult = {
  visibleItems: CategoryItem[];
  categoryItems: CategoryItem[];
  selectedCategoryId: string | null;
  setSelectedCategoryId: (id: string) => void;
};

/**
 * Returns the number of active filters within a set of columns, delegating the
 * per-value "is active" check to the canonical `isFilterValueActive` predicate.
 */
export const countActiveFilters = (columns: EagColumn[], filterState: Filters): number =>
  columns.filter((col) => col.filter && isFilterValueActive(filterState[col.key])).length;

/** Builds a single CategoryItem from its accumulated filterable columns. */
const toCategoryItem = (
  id: string,
  label: string,
  color: CategoryColorScheme,
  columns: EagColumn[],
  filterState: Filters
): CategoryItem => ({
  id,
  label,
  color,
  activeCount: countActiveFilters(columns, filterState),
  childLabels: columns.map((col) => normalizeSearchText(col.headerName)),
});

/**
 * Builds a flat list of internal category items (with childLabels for search)
 * from the columnConfig, merging repeated groupIds.
 *
 * Children of a repeated groupId are accumulated in first-encounter order; the
 * group's label/color come from its FIRST occurrence and activeCount/childLabels
 * are derived once over the full accumulated child set.
 *
 * Exported so tests can verify the build logic independently.
 */
export const buildCategoryItems = (
  columnConfig: EagColumnOrGroup[],
  filterState: Filters
): CategoryItem[] => {
  const groups = new Map<
    string,
    {label: string; color: CategoryColorScheme; columns: EagColumn[]}
  >();
  const standaloneColumns: EagColumn[] = [];

  for (const item of columnConfig) {
    if (isEagColumnGroup(item)) {
      const group = item as EagColumnGroup;
      const filterableChildren = group.children.filter(
        (col): col is EagColumn => col != null && !!col.filter && col.lockedHidden !== true
      );

      const existing = groups.get(group.groupId);
      if (existing) {
        // Repeated groupId: accumulate children; keep the first instance's label/color.
        existing.columns.push(...filterableChildren);
      } else {
        groups.set(group.groupId, {
          label: group.headerName,
          color: getCategoryColor(group.groupId),
          columns: [...filterableChildren],
        });
      }
    } else {
      const col = item as EagColumn;
      if (col.filter && col.lockedHidden !== true) {
        standaloneColumns.push(col);
      }
    }
  }

  const items: CategoryItem[] = [];
  for (const [groupId, {label, color, columns}] of groups) {
    items.push(toCategoryItem(groupId, label, color, columns, filterState));
  }

  if (standaloneColumns.length > 0) {
    items.push(
      toCategoryItem(
        STANDALONE_CATEGORY_ID,
        STANDALONE_CATEGORY_LABEL,
        'neutral',
        standaloneColumns,
        filterState
      )
    );
  }

  return items;
};

/**
 * Manages filter category selection state including:
 * - Building the full category item list from columnConfig + filterState
 * - Filtering visible items by searchTerm (category label or child column labels)
 * - Auto-selecting the first available category on mount
 * - Auto-correcting selection when searchTerm changes and current selection
 *   is no longer in the visible set
 */
export function useFilterCategorySelection(
  params: UseFilterCategorySelectionParams
): UseFilterCategorySelectionResult {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  const allItems = buildCategoryItems(params.columnConfig, params.filterState);

  const visibleItems = filterCategoriesBySearch(allItems, params.searchTerm);

  // Auto-select first visible item on mount.
  useOnMount(() => {
    if (visibleItems.length > 0) {
      setSelectedCategoryId(visibleItems[0].id);
    }
  });

  // Derived effective selection — avoids setState-during-render.
  // When the current selection is no longer in the visible set, fall back to the first visible item.
  const effectiveSelectedCategoryId =
    selectedCategoryId !== null &&
    visibleItems.some((item: CategoryItem) => item.id === selectedCategoryId)
      ? selectedCategoryId
      : (visibleItems[0]?.id ?? null);

  return {
    visibleItems,
    categoryItems: allItems,
    selectedCategoryId: effectiveSelectedCategoryId,
    setSelectedCategoryId,
  };
}
